import React, { useEffect, useState } from "react";
import { Button, Alert } from "reactstrap";
import Highlight from "../components/Highlight";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { getConfig } from "../config";
import Loading from "../components/Loading";

export const ExternalApiComponent = () => {
  const config = getConfig();
  const { apiOrigin = "http://localhost:3001", audience, organization } = config;
  const resolvedAudience = audience;
  const missingAudienceMessage =
    "Missing API audience configuration. Update auth_config.json with your API identifier.";
  const isAudienceMissing = !resolvedAudience;

  const attributesEndpoint = new URL(
    "/prd/egs/user/entityservice/rest/query/describe/Attributes.json?appName=ACCOUNT_SUMMARY",
    apiOrigin
  ).toString();

  const [state, setState] = useState({
    showResult: false,
    apiMessage: "",
    error: null,
    isLoading: false,
  });

  const { getAccessTokenSilently, loginWithPopup, getAccessTokenWithPopup } =
    useAuth0();

  const authorizationParams = {
    audience: resolvedAudience,
    scope: "Egs.Read",
    ...(organization ? { organization } : null),
  };

  const fetchAttributesWithToken = async (token) => {
    const response = await fetch(attributesEndpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    const responseData = await response.json();

    setState((prev) => ({
      ...prev,
      showResult: true,
      apiMessage: responseData,
      error: null,
    }));
  };

  const resetBeforeRequest = () => {
    setState((prev) => ({
      ...prev,
      showResult: false,
      apiMessage: "",
      error: null,
      isLoading: true,
    }));
  };

  const finishRequest = () => {
    setState((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  useEffect(() => {
    if (isAudienceMissing) {
      setState((prev) => ({
        ...prev,
        error: missingAudienceMessage,
      }));
    }
  }, [isAudienceMissing]);

  const handleConsent = async (event) => {
    event?.preventDefault();

    if (isAudienceMissing) {
      setState((prev) => ({
        ...prev,
        error: missingAudienceMessage,
      }));
      return;
    }

    try {
  await getAccessTokenWithPopup({ authorizationParams });
      setState((prev) => ({
        ...prev,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error.error || error.message,
      }));
      return;
    }

    await callApi();
  };

  const handleLoginAgain = async (event) => {
    event?.preventDefault();

    if (isAudienceMissing) {
      setState((prev) => ({
        ...prev,
        error: missingAudienceMessage,
      }));
      return;
    }

    try {
  await loginWithPopup({ authorizationParams });
      setState((prev) => ({
        ...prev,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: error.error || error.message,
      }));
      return;
    }

    await callApi();
  };

  const callApi = async (event) => {
    event?.preventDefault();

    if (isAudienceMissing) {
      setState((prev) => ({
        ...prev,
        error: missingAudienceMessage,
      }));
      return;
    }

    resetBeforeRequest();

    try {
      const token = await getAccessTokenSilently({
        authorizationParams,
        cacheMode: "cache-first",
      });
      await fetchAttributesWithToken(token);
      finishRequest();
    } catch (error) {
      const errorCode = error.error || error.message;

      if (error.error === "consent_required" || error.error === "login_required") {
        setState((prev) => ({
          ...prev,
          error: error.error,
          isLoading: false,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        error: errorCode,
        isLoading: false,
      }));
    }
  };

  const handle = (event, fn) => {
    event.preventDefault();
    fn(event);
  };

  return (
    <>
      <div className="mb-5">
        {state.error === "consent_required" && !isAudienceMissing && (
          <Alert color="warning">
            You need to{" "}
            <a
              href="#/"
              className="alert-link"
              onClick={(e) => handle(e, handleConsent)}
            >
              consent to get access to users api
            </a>
          </Alert>
        )}

        {state.error === "login_required" && !isAudienceMissing && (
          <Alert color="warning">
            You need to{" "}
            <a
              href="#/"
              className="alert-link"
              onClick={(e) => handle(e, handleLoginAgain)}
            >
              log in again
            </a>
          </Alert>
        )}

        {state.error && !isAudienceMissing &&
          state.error !== "consent_required" &&
          state.error !== "login_required" && (
            <Alert color="danger" data-testid="api-error">
              {state.error}
            </Alert>
          )}

        {isAudienceMissing && (
          <Alert color="info" data-testid="api-warning">
            Configure the API audience in <code>auth_config.json</code> to enable external API calls.
          </Alert>
        )}

        <h1>External API</h1>
        <p className="lead">
          Get some entities via Attributes API by clicking the button below.
        </p>

        <p>
          his will call the Attributes API to retrieve a list of account attributes. An access token with the appropriate scope (Egs.Read) is sent as part of the request's `Authorization` header and the API will validate it using the custom API audience.
        </p>

        <Button
          color="primary"
          className="mt-5"
          type="button"
          onClick={callApi}
          disabled={state.isLoading || isAudienceMissing}
        >
          Get Some Account Attributes
        </Button>
      </div>

      <div className="result-block-container">
        {state.isLoading && (
          <div className="result-block" data-testid="api-loading">
            <h6 className="muted">Loading</h6>
            <Highlight>
              <span>Requesting attributes...</span>
            </Highlight>
          </div>
        )}

        {state.showResult && !state.isLoading && (
          <div className="result-block" data-testid="api-result">
            <h6 className="muted">Result</h6>
            <Highlight>
              <span>{JSON.stringify(state.apiMessage, null, 2)}</span>
            </Highlight>
          </div>
        )}
      </div>
    </>
  );
};

export default withAuthenticationRequired(ExternalApiComponent, {
  onRedirecting: () => <Loading />,
});
